import type { NextPage } from 'next';
import Head from 'next/head';
import Layout from '@/components/Layout';
import MetricCard from '@/components/MetricCard';
import { useAuth } from '@/lib/authContext';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { 
  getMetricasDashboard, 
  getActividadReciente,
  getAIAccountantStats,
  getAdvisorPerformance,
  getDashboardAlerts,
  type AIAccountantStats,
  type AdvisorPerformance,
  type DashboardAlert,
} from '@/lib/supabaseService';

const AdminDashboard: NextPage = () => {
  const { user, loading } = useAuth();
  const router = useRouter();
  const [metricas, setMetricas] = useState({
    documentosPendientes: 0,
    facturasIA: 0,
    incidenciasAbiertas: 0,
    clientesActivos: 0,
    asesoresActivos: 0,
  });
  const [actividadReciente, setActividadReciente] = useState<any[]>([]);
  const [aiStats, setAiStats] = useState<AIAccountantStats>({
    procesadasCorrectamente: 0,
    conErroresIA: 0,
    pendientesAnalisis: 0,
    docsEsteMes: 0,
    tiempoMedio: '0s',
  });
  const [advisorPerformance, setAdvisorPerformance] = useState<AdvisorPerformance[]>([]);
  const [alerts, setAlerts] = useState<DashboardAlert[]>([]);
  const [loadingDatos, setLoadingDatos] = useState(true);

  useEffect(() => {
    if (!loading && (!user || user.rol !== 'admin')) {
      router.push('/');
    }
  }, [user, loading, router]);

  useEffect(() => {
    const cargarDatos = async () => {
      if (!user || user.rol !== 'admin') return;

      try {
        const [metricasDb, actividadDb, aiStatsDb, advisorPerfDb, alertsDb] = await Promise.all([
          getMetricasDashboard(user?.id, user?.rol),
          getActividadReciente(10),
          getAIAccountantStats(),
          getAdvisorPerformance(3),
          getDashboardAlerts(),
        ]);
        setMetricas(metricasDb);
        setActividadReciente(actividadDb);
        setAiStats(aiStatsDb);
        setAdvisorPerformance(advisorPerfDb);
        setAlerts(alertsDb);
      } catch (error) {
        console.error('Error cargando métricas o actividad:', error);
      } finally {
        setLoadingDatos(false);
      }
    };

    cargarDatos();
  }, [user]);

  if (loading || !user || user.rol !== 'admin' || loadingDatos) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-calm-bg">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-admin border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm text-calm-muted">Cargando panel…</p>
        </div>
      </div>
    );
  }

  const alertBorderColor = (tipo: string) => {
    if (tipo === 'error') return 'border-l-error';
    if (tipo === 'warning') return 'border-l-warning';
    return 'border-l-info';
  };

  const alertBadge = (tipo: string) => {
    if (tipo === 'error') return 'badge-error';
    if (tipo === 'warning') return 'badge-warning';
    return 'badge-info';
  };

  const activityIcon = (tipo: string) => {
    switch (tipo) {
      case 'documento': return <span className="material-symbols-outlined text-sm">description</span>;
      case 'incidencia': return <span className="material-symbols-outlined text-sm">warning</span>;
      case 'contabilizacion': return <span className="material-symbols-outlined text-sm">trending_up</span>;
      default: return <span className="material-symbols-outlined text-sm">schedule</span>;
    }
  };

  const advisorColors = ['bg-admin/10 text-admin', 'bg-asesor/10 text-asesor', 'bg-cliente/10 text-cliente'];

  return (
    <>
      <Head>
        <title>Dashboard Admin — Externaliza2</title>
        <link rel="icon" href="/logo.png" />
      </Head>
      <Layout rol="admin">
        <div className="h-screen lg:h-screen overflow-y-auto scrollbar-thin">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 space-y-8">

            {/* Header */}
            <div className="animate-fade-in">
              <h1 className="text-5xl font-light font-display text-calm-text">Panel de Control</h1>
              <p className="text-calm-muted mt-2">Vista general de toda la asesoría</p>
            </div>

            {/* Metric cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="animate-slide-up stagger-1">
                <MetricCard
                  titulo="DOCS PENDIENTES"
                  valor={metricas.documentosPendientes}
                  descripcion="Sin contabilizar"
                  icono={<span className="material-symbols-outlined text-xl">description</span>}
                  accent="bg-warning"
                  href="/admin/documentos"
                />
              </div>
              <div className="animate-slide-up stagger-2">
                <MetricCard
                  titulo="INCIDENCIAS"
                  valor={metricas.incidenciasAbiertas}
                  descripcion="Requieren atención"
                  icono={<span className="material-symbols-outlined text-xl">warning</span>}
                  accent="bg-error"
                  href="/admin/incidencias"
                />
              </div>
              <div className="animate-slide-up stagger-3">
                <MetricCard
                  titulo="CLIENTES ACTIVOS"
                  valor={metricas.clientesActivos}
                  descripcion="Con actividad reciente"
                  icono={<span className="material-symbols-outlined text-xl">group</span>}
                  accent="bg-asesor"
                  href="/admin/clientes-asesores"
                />
              </div>
              <div className="animate-slide-up stagger-4">
                <MetricCard
                  titulo="ASESORES"
                  valor={metricas.asesoresActivos}
                  descripcion="Trabajando este mes"
                  icono={<span className="material-symbols-outlined text-xl">work</span>}
                  accent="bg-admin"
                  href="/admin/clientes-asesores?vista=asesores"
                />
              </div>
            </div>

            {/* Two-column layout: Activity + Alerts */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 animate-slide-up stagger-5">

              {/* Actividad Reciente */}
              <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-8">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="section-title flex items-center gap-2">
                    <span className="material-symbols-outlined text-lg text-steel-azure-400">schedule</span>
                    Actividad Reciente
                  </h2>
                </div>
                {actividadReciente.length > 0 ? (
                  <div className="space-y-1">
                    {actividadReciente.slice(0, 6).map((actividad) => (
                      <div
                        key={actividad.id}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-huge hover:bg-calm-bg transition-colors group"
                      >
                        <div className="w-7 h-7 rounded-huge bg-calm-bg flex items-center justify-center text-calm-muted flex-shrink-0">
                          {activityIcon(actividad.tipo)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-calm-text truncate">{actividad.descripcion}</p>
                          <p className="text-xs text-calm-muted mt-0.5">
                            {actividad.usuario} · {new Date(actividad.fecha).toLocaleDateString('es-ES')}
                          </p>
                        </div>
                        <span className="material-symbols-outlined text-sm text-calm-muted opacity-0 group-hover:opacity-100 transition-opacity">chevron_right</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <span className="material-symbols-outlined text-[32px] text-calm-muted block mx-auto mb-2">schedule</span>
                    <p className="text-sm text-calm-muted">Sin actividad reciente</p>
                  </div>
                )}
              </div>

              {/* Alertas */}
              <div className="bg-gradient-to-br from-white to-rose-50/20 border border-rose-200/40 rounded-huge p-8">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="section-title flex items-center gap-2">
                    <span className="material-symbols-outlined text-lg text-rose-400">notifications</span>
                    Alertas
                  </h2>
                  {alerts.length > 0 && (
                    <span className="badge badge-error">{alerts.length}</span>
                  )}
                </div>
                {alerts.length > 0 ? (
                  <div className="space-y-2">
                    {alerts.map((alert) => (
                      <div
                        key={alert.id}
                        className={`p-3.5 bg-calm-bg rounded-huge border-l-[3px] ${alertBorderColor(alert.tipo)}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`badge ${alertBadge(alert.tipo)} text-[10px] py-0.5`}>
                                {alert.tipo.toUpperCase()}
                              </span>
                            </div>
                            <p className="text-sm font-light text-calm-text">{alert.titulo}</p>
                            <p className="text-xs text-calm-muted mt-0.5">{alert.mensaje}</p>
                          </div>
                          {alert.accion && (
                            <a href={alert.href || '#'} className="ghost-btn flex-shrink-0 text-xs">
                              {alert.accion}
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <span className="material-symbols-outlined text-[32px] text-calm-muted block mx-auto mb-2">notifications</span>
                    <p className="text-sm text-calm-muted">Sin alertas pendientes</p>
                  </div>
                )}
              </div>
            </div>

            {/* Rendimiento por Asesor */}
            {advisorPerformance.length > 0 && (
              <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8 animate-slide-up stagger-6">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="section-title flex items-center gap-2">
                    <span className="material-symbols-outlined text-lg text-fresh-sky-400">group</span>
                    Rendimiento por Asesor
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {advisorPerformance.map((advisor, i) => (
                    <div key={advisor.id} className="flex items-center gap-4 p-4 rounded-huge bg-gradient-to-br from-fresh-sky-50/30 to-white border border-fresh-sky-100/60 hover:shadow-sm hover:border-fresh-sky-200/60 transition-all">
                      <div className={`w-10 h-10 rounded-huge flex items-center justify-center text-sm font-light ${advisorColors[i % advisorColors.length]}`}>
                        {advisor.nombre?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-light text-calm-text truncate">{advisor.nombre}</p>
                        <p className="text-xs text-calm-muted">{advisor.clientesCount} clientes</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-light font-display text-calm-text">{advisor.documentosEsteMes}</p>
                        <p className="text-[10px] text-calm-muted uppercase tracking-wider">docs/mes</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </Layout>
    </>
  );
};

export default AdminDashboard;
